/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useCallback, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { Button, Form, Icon } from 'semantic-ui-react';
import PropTypes from 'prop-types';
import { isEmpty } from 'lodash';
import { read, utils } from 'xlsx';
import jpegIcon from 'assets/images/jpegIcon.svg';
import folderImage from 'assets/images/file.svg';
import pngIcon from 'assets/images/pngIcon.svg';
import gifIcon from 'assets/images/gifIcon.svg';
import pdfIcon from 'assets/images/pdfIcon.svg';
import csvFileExtension from 'assets/images/csvFileExtension.svg';
import xlsFileExtension from 'assets/images/xlsFileExtension.svg';
import xlsxFileExtension from 'assets/images/xlsxFileExtension.svg';

import SpreadsheetPreviewModal from './SpreadsheetPreviewModal';
import './index.scss';

const formatFileSize = bytes => {
  if (!bytes) return '0 B';
  if (bytes < 1024) return `${bytes} B`;
  const kb = Math.round(bytes / 1024);
  if (kb < 1024) return `${kb} KB`;
  const mb = (kb / 1024).toFixed(1);
  return `${mb} MB`;
};

const fileIconMap = {
  csv: csvFileExtension,
  xls: xlsFileExtension,
  xlsx: xlsxFileExtension,
  pdf: pdfIcon,
  png: pngIcon,
  jpg: jpegIcon,
  jpeg: jpegIcon,
  gif: gifIcon,
};

const MyDropzone = ({
  setFile,
  file,
  label,
  showPreview = true,
  accept = 'image/*',
  templateUrl,
  variant,
}) => {
  const [urlFile, setUrlFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState({
    columns: [],
    rows: [],
  });

  const onDrop = useCallback(acceptedFiles => {
    setFile(acceptedFiles[0]);
  }, []);

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept,
  });

  const removeFile = () => setFile(null);

  const ext = file?.name
    ?.split('.')
    .pop()
    ?.toLowerCase();
  const isSpreadsheet = ['xlsx', 'xls', 'csv'].includes(ext);

  const handlePreview = () => {
    const reader = new FileReader();
    reader.readAsBinaryString(file);
    reader.onload = event => {
      const workbook = read(event.target.result, { type: 'binary' });
      const sheet = workbook.SheetNames[0];
      const rows = utils.sheet_to_json(workbook.Sheets[sheet], {
        defval: '',
      });
      if (rows.length > 0) {
        setPreviewData({ columns: Object.keys(rows[0]), rows });
      }
      setPreviewOpen(true);
    };
  };

  useEffect(() => {
    if (!file || isEmpty(file)) {
      setUrlFile(null);
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    setUrlFile(objectUrl);

    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  return (
    <>
      <Form.Field>
        {label && (
          <div className="dropzone-label-row">
            <span className="dropzone-label">{label}</span>
            {templateUrl && (
              <a
                href={templateUrl}
                download
                className="dropzone-template-link"
              >
                List template
              </a>
            )}
          </div>
        )}
        {file && !isEmpty(file) ? (
          <>
            {showPreview && !isSpreadsheet && (
              <div className="uploaded_field">
                <div
                  {...getRootProps()}
                  className="uploaded_field__file"
                >
                  <img
                    src={urlFile}
                    alt=""
                    className="uploaded_field__file--image"
                  />
                  <input
                    className="uploaded_field__file--attachment"
                    {...getInputProps()}
                  />
                </div>
              </div>
            )}
            <div className="uploaded_field__file--name">
              <div className="uploaded_field__file--info">
                <img
                  src={fileIconMap[ext] || folderImage}
                  alt={ext}
                  className="uploaded_field__file--icon"
                />
                <div className="uploaded_field__file--details">
                  <span className="uploaded_field__file--filename">
                    {file?.name}
                  </span>
                  <span className="uploaded_field__file--meta">
                    {formatFileSize(file?.size)}
                  </span>
                </div>
              </div>
              <div className="uploaded_field__file--actions">
                {isSpreadsheet && (
                  <Button
                    type="button"
                    primary
                    onClick={handlePreview}
                  >
                    Preview
                  </Button>
                )}
                <Icon
                  name="trash alternate outline"
                  onClick={removeFile}
                />
              </div>
            </div>
            <SpreadsheetPreviewModal
              open={previewOpen}
              onClose={() => setPreviewOpen(false)}
              fileName={file?.name}
              data={previewData}
            />
          </>
        ) : (
          <div className="input_field" {...getRootProps()}>
            <img src={folderImage} alt="" className="file" />
            <input {...getInputProps()} required />
            <p>
              Drag and drop here or{' '}
              <span className="link">browse</span>
            </p>
            <div className="format">
              <span>Supported format:</span>
              {variant === 'spreadsheet' ? (
                <div className="file_icons">
                  <img src={csvFileExtension} alt="csv" />
                  <img src={xlsFileExtension} alt="xls" />
                  <img src={xlsxFileExtension} alt="xlsx" />
                </div>
              ) : variant === 'pdf' ? (
                <div className="file_icons">
                  <img src={pngIcon} alt="png" />
                  <img src={jpegIcon} alt="jpeg" />
                  <img src={pdfIcon} alt="pdf" />
                </div>
              ) : (
                <div className="file_icons">
                  <img src={gifIcon} alt="" />
                  <img src={pngIcon} alt="" />
                  <img src={jpegIcon} alt="" />
                </div>
              )}
            </div>
          </div>
        )}
      </Form.Field>
    </>
  );
};

MyDropzone.propTypes = {
  setFile: PropTypes.func.isRequired,
  file: PropTypes.instanceOf(Object),
  accept: PropTypes.string,
  label: PropTypes.string,
  showPreview: PropTypes.bool,
  templateUrl: PropTypes.string,
  variant: PropTypes.oneOf(['image', 'spreadsheet', 'pdf']),
};

MyDropzone.defaultProps = {
  file: {},
  accept: '',
  label: '',
  showPreview: true,
  templateUrl: null,
  variant: 'image',
};

export default MyDropzone;
